import { useState } from "react";
import { Formik, Form } from "formik";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { BrandedSplitLayout } from "../../../branding/components/BrandedSplitLayout";
import { BrandedFormHeader } from "../../../branding/components/BrandedFormHeader";
import { BrandedSecondaryAction } from "../../../branding/components/BrandedSecondaryAction";
import { BrandedResultMessage } from "../../../branding/components/BrandedResultMessage";
import { BrandedTextField } from "../../../branding/components/BrandedTextField";
import { BrandedButton } from "../../../branding/components/BrandedButton";
import { PasswordStrengthBar } from "../../../components/PasswordStrengthBar";
import { routesConfig } from "../../../config/routes";
import { useContextualNavigate } from "../../../hooks/ContextualNavigations";
import { showPass } from "../../../utils";

import { validationSchemaChangePassword } from "../form/validationSchema";
import { initialValuesFormikUpdatePassword } from "../form/initialValues";
import { ChangePasswordFormFields } from "../form/interfaceValues";
import { useChangePasswordSubmit } from "../ChangePasswordDefault/hook/useChangePasswordSubmit";

// ChangePassword Branded (reset de senha via link do e-mail), padrão D12. Reusa
// useChangePasswordSubmit — mesma lógica de auth da Default (email/verification_code vêm da
// URL). Campos = BrandedTextField; botão = BrandedButton; medidor compacto.
const ChangePasswordBranded = (): JSX.Element => {
  const navigate = useContextualNavigate();
  const { handleSubmit, successChangePassword } = useChangePasswordSubmit();
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const goToLogin = () => navigate(routesConfig.LOGIN);

  if (successChangePassword) {
    return (
      <BrandedSplitLayout>
        <BrandedResultMessage
          title="Senha cadastrada"
          message="Sua nova senha foi salva com sucesso."
          primaryLabel="Fazer login"
          onPrimary={goToLogin}
        />
      </BrandedSplitLayout>
    );
  }

  return (
    <Formik
      validateOnMount
      initialValues={initialValuesFormikUpdatePassword}
      validationSchema={validationSchemaChangePassword}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isValid, isSubmitting, handleChange, handleBlur }) => (
        <Form>
          <BrandedSplitLayout>
            <BrandedFormHeader
              title="Criar nova senha"
              subtitle="Defina sua nova senha de acesso."
            />

            <BrandedTextField
              type={showPass(showNew)}
              name={ChangePasswordFormFields.newPass}
              label="Nova senha"
              value={values.newPass}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.newPass && Boolean(errors.newPass)}
              helperText={touched.newPass && errors.newPass}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowNew((s) => !s)}>
                      {showNew ? <VisibilityOff color="primary" /> : <Visibility color="primary" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <BrandedTextField
              type={showPass(showConfirm)}
              name={ChangePasswordFormFields.confirmPass}
              label="Confirme a nova senha"
              value={values.confirmPass}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.confirmPass && Boolean(errors.confirmPass)}
              helperText={touched.confirmPass && errors.confirmPass}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowConfirm((s) => !s)}>
                      {showConfirm ? <VisibilityOff color="primary" /> : <Visibility color="primary" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <PasswordStrengthBar
              newPass={values.newPass}
              newPassConfirmation={values.confirmPass}
              compact
            />

            <BrandedButton type="submit" loading={isSubmitting} disabled={!isValid}>
              Salvar nova senha
            </BrandedButton>

            <BrandedSecondaryAction actions={[{ label: "Voltar para o login", to: routesConfig.LOGIN }]} />
          </BrandedSplitLayout>
        </Form>
      )}
    </Formik>
  );
};

export default ChangePasswordBranded;
